import type { WidgetConfig } from "@workspace/contracts/embed"

/**
 * A config's LOOK, as the dashboard preview computes it (`widget-preview.tsx`)
 * - lifted verbatim so the page and the playground cannot disagree on how an
 * appearance becomes colours. Pure: numbers and strings in, strings out.
 *
 * `hexToRgb` / `readableOn` are `@workspace/ui/lib/hex-color` copied, not
 * imported: that package carries React and Tailwind, and this bundle
 * carries neither.
 */

/** `"#3b46df"` (or `"3b46df"`) -> `{ r, g, b }`, each 0-255. */
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const c = hex.replace("#", "")
  return {
    r: parseInt(c.slice(0, 2), 16),
    g: parseInt(c.slice(2, 4), 16),
    b: parseInt(c.slice(4, 6), 16),
  }
}

/**
 * Black or white, whichever stays legible on `hex`. Uses the perceived-
 * luminance weights (0.299/0.587/0.114); the 0.62 threshold is tuned so a
 * mid-bright accent still gets dark text.
 */
export function readableOn(hex: string): string {
  const { r, g, b } = hexToRgb(hex)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.62 ? "#15171e" : "#ffffff"
}

/** The ink triplet for text over a light or a dark ground - `rgb(${ink})`. */
export const inkOn = (dark: boolean) => (dark ? "245,245,241" : "21,23,30")

export interface Look {
  /** The solid accent hex - borders and text, where translucency reads as broken. */
  accent: string
  /** The accent with its alpha applied - every FILL. */
  accentFill: string
  /** Black or white, whichever reads on the accent. */
  accentText: string
  /** Corner radius, px. */
  rad: number
  /** The chat stream's frosted finish. */
  isGlass: boolean
  /**
   * The chat bubble in Messenger's chrome (`preset: "facebook-messenger"`):
   * `chat-messenger.tsx` draws from `MESSENGER`, and the accent, fill and
   * radius here go unused. A preset stored on another variant is inert.
   */
  isMessenger: boolean
  /** The host page reads dark - the ink and the card follow. */
  hostDark: boolean
  /** `r,g,b` for text on the host. */
  ink: string
  /** The card behind a panel, a popup, a testimonial. */
  surfaceBg: string
  /** `bottom-right` / `bottom-left` as the CSS side, with its offset. */
  side: { right: number } | { left: number }
  offsetY: number
}

export function lookOf(config: WidgetConfig, hostDark: boolean): Look {
  const a = config.appearance
  const accent = a.accent
  // The accent's opacity from the alpha rail (0-100), applied to FILLS;
  // borders and text keep the solid hex.
  const accentFill = (() => {
    if (a.alpha >= 100) return accent
    const { r, g, b } = hexToRgb(accent)
    return `rgba(${r},${g},${b},${a.alpha / 100})`
  })()
  return {
    accent,
    accentFill,
    accentText: readableOn(accent),
    rad: a.radius,
    isGlass:
      config.type === "chat" &&
      config.variant === "stream" &&
      a.skin === "glass",
    isMessenger:
      config.type === "chat" &&
      config.variant === "bubble" &&
      config.preset === "facebook-messenger",
    hostDark,
    ink: inkOn(hostDark),
    surfaceBg: hostDark ? "#181b22" : "#ffffff",
    side:
      a.position === "bottom-right"
        ? { right: a.offsetX }
        : { left: a.offsetX },
    offsetY: a.offsetY,
  }
}

/**
 * The customer's class for a named part, from `appearance.parts` - applied
 * beside the runtime's own `uq-*` class so their stylesheet can target it.
 */
export function partClass(
  config: WidgetConfig,
  part: string,
  own?: string
): string {
  const custom = config.appearance.parts[part]
  const classes = [`uq-${part}`]
  if (own) classes.push(own)
  if (custom) classes.push(custom.trim())
  return classes.join(" ")
}
